import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Button, 
  Card, 
  Badge, 
  Carousel, 
  ProgressBar,
  Nav,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/gatesection.css'; // We'll create this for custom styling

const GatePreparationCarousel = () => {
  const [index, setIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Update window width when resized
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Responsive helpers
  const isMobile = windowWidth < 576;
  const isTablet = windowWidth >= 576 && windowWidth < 992;

  // Handle carousel navigation
  const handleSelect = (selectedIndex) => {
    setIndex(selectedIndex);
  };

  // The GATE subjects data
  const gateSubjects = [
    {
      id: 1,
      name: "Computer Science & Engineering",
      description: "Prepare for GATE CSE with comprehensive coverage of Data Structures, Algorithms, Computer Networks, Operating Systems, DBMS, and more. Our expert-designed curriculum covers all topics with practice tests and mock exams.",
      bgColor: "primary",
      icon: "💻",
      features: ['Expert Faculty', 'Live Doubt Sessions', 'Mock Tests', 'Study Material', 'Live Classes', 'Performance Analytics']
    },
    {
      id: 2,
      name: "Electrical & Electronics Engineering",
      description: "Master GATE EE/ECE with focused modules on Circuit Theory, Power Systems, Control Systems, Signals & Systems, and Digital Electronics. Learn from industry experts with hands-on problem solving.",
      bgColor: "success",
      icon: "⚡",
      features: ['Industry Experts', 'Practical Labs', 'Weekly Tests', 'Video Lectures', 'Study Notes', 'Problem Solving Sessions']
    },
    {
      id: 3,
      name: "Mechanical Engineering",
      description: "Excel in GATE ME with our specialized courses covering Thermodynamics, Engineering Mechanics, Manufacturing Processes, Machine Design, and Fluid Mechanics. Includes solved papers and concept videos.",
      bgColor: "danger",
      icon: "⚙️",
      features: ['Concept Videos', 'Solved Papers', 'Practice Problems', 'Mentorship', 'Topic Tests', 'Revision Sessions']
    },
    {
      id: 4,
      name: "Aerospace Engineering",
      description: "Prepare for GATE Aerospace with in-depth coverage of Aerodynamics, Aircraft Structures, Propulsion, Flight Mechanics, and Space Dynamics. Get mentored by experienced aerospace professionals.",
      bgColor: "indigo",
      icon: "✈️",
      features: ['Industry Mentors', 'Specialized Content', 'Weekly Workshops', 'Formula Sheets', 'Question Bank', 'Career Guidance']
    },
    {
      id: 5,
      name: "Civil Engineering",
      description: "Ace GATE CE with structured learning of Structural Engineering, Geotechnical Engineering, Transportation Engineering, Environmental Engineering, and Surveying. Includes regular assessments and doubt clearing sessions.",
      bgColor: "warning",
      icon: "🏗️",
      features: ['Practice Sessions', 'Doubt Clearing', 'Topic-wise Tests', 'Comprehensive Notes', 'Previous Papers', 'Visual Learning']
    },
    {
      id: 6,
      name: "Data Science",
      description: "Master GATE DS with our comprehensive curriculum covering Statistics, Machine Learning, Data Mining, Big Data Technologies, and Probability. Learn through hands-on projects and real-world case studies.",
      bgColor: "info",
      icon: "📊",
      features: ['Hands-on Projects', 'Case Studies', 'Python Programming', 'Statistical Analysis', 'Algorithm Practice', 'Mock Interviews']
    },
    {
      id: 7,
      name: "Artificial Intelligence",
      description: "Prepare for GATE AI with focused modules on Machine Learning, Natural Language Processing, Computer Vision, Neural Networks, and Knowledge Representation. Includes programming exercises and algorithm implementation.",
      bgColor: "purple",
      icon: "🤖",
      features: ['Programming Labs', 'Algorithm Implementation', 'Research Papers', 'Coding Challenges', 'Project Work', 'Industry Connect']
    },
    {
      id: 8,
      name: "Biotechnology",
      description: "Excel in GATE BT with comprehensive coverage of Biochemistry, Molecular Biology, Genetic Engineering, Bioprocess Engineering, and Immunology. Our program includes virtual lab simulations and case-based learning.",
      bgColor: "teal",
      icon: "🧬",
      features: ['Virtual Labs', 'Case-based Learning', 'Research Methodology', 'Industry Applications', 'Scientific Writing', 'Exam Strategy']
    },
    {
      id: 9,
      name: "Geology & Geophysics",
      description: "Master GATE GG with our specialized courses covering Mineralogy, Petrology, Stratigraphy, Structural Geology, and Geophysical Methods. Includes field study methodology and practical interpretation techniques.",
      bgColor: "gray",
      icon: "🌋",
      features: ['Field Study Methods', 'Mineral Identification', 'Map Interpretation', 'Geological Modeling', 'Practical Sessions', 'Expert Guidance']
    },
    {
      id: 10,
      name: "Instrumentation Engineering",
      description: "Prepare for GATE IN with comprehensive coverage of Sensors, Control Systems, Signal Processing, Industrial Instrumentation, and Process Control. Our program includes virtual labs and industry applications.",
      bgColor: "orange",
      icon: "🔧",
      features: ['Virtual Labs', 'Industrial Case Studies', 'Signal Analysis', 'Control System Design', 'Calibration Techniques', 'Process Optimization']
    },
    {
      id: 11,
      name: "Electronics & Communication",
      description: "Excel in GATE EC with focused learning on Digital Electronics, Analog Circuits, Communication Systems, Electromagnetic Theory, and VLSI. Includes circuit simulation and signal processing labs.",
      bgColor: "cyan",
      icon: "📡",
      features: ['Circuit Simulation', 'Signal Processing Labs', 'Design Projects', 'Component Analysis', 'System Debugging', 'Interview Preparation']
    },
    {
      id: 12,
      name: "Chemical Engineering",
      description: "Master GATE CH with our specialized courses covering Chemical Process Principles, Thermodynamics, Heat Transfer, Mass Transfer, and Chemical Reaction Engineering. Includes process simulation and plant design.",
      bgColor: "lime",
      icon: "🧪",
      features: ['Process Simulation', 'Plant Design', 'Reaction Kinetics', 'Transport Phenomena', 'Numerical Methods', 'Safety Protocols'],
      link:"/gate-chemical"
    }
  ];

  // Render features for each subject
  const renderFeatures = (features) => (
    <div className="d-flex flex-wrap gap-2 mt-3">
      {features.map((feature, idx) => (
        <Badge key={idx} bg="light" text="dark" className="feature-badge px-3 py-2">
          <div className="d-flex align-items-center">
            <div className="feature-dot me-2"></div>
            {feature}
          </div>
        </Badge>
      ))}
    </div>
  );

  // Truncate subject name for smaller screens
  const getResponsiveSubjectName = (name) => {
    if (isMobile) return name.split(' ')[0];
    if (isTablet) return name.split(' ').slice(0, 2).join(' ');
    return name;
  };

  return (
    <div className="gate-prep-app">
      <Container fluid className="py-5">
        {/* Header */}
        <Row className="mb-5 text-center">
          <Col>
            <h1 className="display-4 fw-bold">GATE Exam Preparation Programs</h1>
            <div className="title-underline mx-auto my-3"></div>
            <p className="lead text-muted px-md-5 mx-auto" style={{ maxWidth: '800px' }}>
              Expert-led courses to help you excel in your GATE exam preparation. 
              Choose from multiple disciplines and start your journey to success.
            </p>
          </Col>
        </Row>

        {/* Main Carousel */}
        <Row className="justify-content-center mb-5">
          <Col xs={12} md={9} lg={10} xxl={12}>
            <Card className="main-carousel-card border-0 shadow-lg">
              <Carousel 
                activeIndex={index} 
                onSelect={handleSelect}
                indicators={true}
                controls={true}
                interval={2000}
                className="gate-carousel"
              >
                {gateSubjects.map((subject, idx) => (
                  <Carousel.Item key={idx}>
                    <div className={`subject-slide bg-${subject.bgColor}`}>
                      <div className="blur-circle top-right"></div>
                      <div className="blur-circle bottom-left"></div>

                      <Container>
                        <Row>
                          <Col xs={12} md={10} lg={12} xxl={12} className="p-4 p-md-5">
                            <Badge bg="light" text="dark" className="mb-3 px-3 py-2 fw-normal">
                              GATE 2025 Exam
                            </Badge>
                            
                            <h2 className="display-5 fw-bold text-white mb-3">
                              <span className="me-2">{subject.icon}</span>
                              {subject.name}
                            </h2>
                            
                            <p className="subject-description mb-4">
                              {subject.description}
                            </p>
                            
                            <div className="d-flex flex-wrap gap-3 mb-4">
                              <Button variant="outline-light" className="fw-medium d-flex align-items-center">
                                <i className="bi bi-book me-2"></i> GATE Question Papers
                              </Button>
                              <Button variant="light" className="fw-medium d-flex align-items-center"
                              href='/enroll'
                              >
                                <i className="bi bi-mortarboard me-2"></i> Enroll For Program
                              </Button>
                            </div>
                            
                            <Button variant="link" className="text-white p-0 mb-4 d-flex align-items-center"
                            href='/gate_page'
                            >
                                
                              Go to GATE Preparation Page
                              <i className="bi bi-arrow-right ms-2"></i>
                            </Button>
                            
                            <h3 className="h5 fw-medium text-white mt-4 mb-2 d-flex align-items-center">
                              <i className="bi bi-star-fill me-2"></i> Program Features
                            </h3>
                            {renderFeatures(subject.features)}
                          </Col>
                        </Row>
                      </Container>
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>

              {/* Progress indicator */}
              <div className="px-3 py-2 bg-dark text-light d-flex justify-content-between align-items-center">
                <small>Subject {index + 1} of {gateSubjects.length}</small>
                <ProgressBar 
                  now={((index + 1) / gateSubjects.length) * 100} 
                  className="flex-grow-1 mx-3" 
                  variant={gateSubjects[index].bgColor}
                />
                <small>{Math.round(((index + 1) / gateSubjects.length) * 100)}% Complete</small>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Subject Navigation Pills */}
        <Row className="justify-content-center mb-2">
          <Col xs={12} md={10} lg={12} >
            <Nav className="subject-nav flex-wrap justify-content-center gap-2">
              {gateSubjects.map((subject, idx) => (
                <OverlayTrigger
                  key={idx}
                  placement="top"
                  overlay={<Tooltip>{subject.name}</Tooltip>}
                >
                  <Nav.Item>
                    <Nav.Link 
                      onClick={() => setIndex(idx)}
                      className={`subject-pill ${index === idx ? `active bg-${subject.bgColor}` : ''}`}
                    >
                      <span className="me-1">{subject.icon}</span>
                      <a href={gateSubjects.link}>{''}</a>
                      {getResponsiveSubjectName(subject.name)}
                    </Nav.Link>
                  </Nav.Item>
                </OverlayTrigger>
              ))}
            </Nav>
          </Col>
        </Row>

        {/* Why Choose Us Section */}
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xxl={9}>
            <Card className="border-0 bg-dark text-light shadow-lg p-4">
              <Card.Body>
                <h2 className="h2 mb-4">Why Choose Our GATE Preparation Program?</h2>
                
                <Row xs={1} md={2} lg={3} className="g-4">
                  {[
                    { 
                      title: "Expert Faculty from Top IIT's ", 
                      desc: "Learn from top educators and industry experts with years of experience",
                      icon: "bi-mortarboard-fill",
                      color: "primary" 
                    },
                    { 
                      title: "Comprehensive Study Material prepared for competative & Public service Exams", 
                      desc: "Access detailed notes, video lectures, and practice questions",
                      icon: "bi-book-fill",
                      color: "success" 
                    },
                    { 
                      title: "Regular Mock Tests", 
                      desc: "Test your knowledge with regular assessments and full-length mock exams",
                      icon: "bi-check-circle-fill",
                      color: "info" 
                    }
                  ].map((item, idx) => (
                    <Col key={idx}>
                      <Card className="feature-card h-100 bg-light border-0">
                        <Card.Body>
                          <div className={`feature-icon text-${item.color} mb-3`}>
                            <i className={`bi ${item.icon}`}></i>
                          </div>
                          <Card.Title className="h4">{item.title}</Card.Title>
                          <Card.Text>{item.desc}</Card.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GatePreparationCarousel;